import { cx } from '../../lib/cx.ts';

/**
 * Loading states are skeletons that match the final layout, never spinners.
 * The point is that nothing moves when real content arrives: a skeleton with
 * the wrong height is worse than no skeleton at all.
 *
 * The pulse is opacity only. No shimmer sweep, which reads as decoration
 * rather than information.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-sm bg-surface-raised', className)} />;
}

/** Lines of body text. The last line is short, the way real text wraps. */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cx('h-3', index === lines - 1 ? 'w-2/5' : 'w-full')}
        />
      ))}
    </div>
  );
}

/**
 * Mirrors StatTile exactly: an 18px label line, a 36px value line and an
 * 18px delta line, with the same mt-2 gaps and the same padding and border.
 * The rows are wrapped in fixed-height boxes rather than sized by the bars
 * themselves, because the bar is shorter than the line box it stands in.
 *
 * Measured against the real tile: both are 122px. If StatTile's type scale
 * changes, this has to change with it.
 */
export function SkeletonStatTile() {
  return (
    <div className="surface-lift rounded-lg border border-border bg-surface p-4">
      <div className="flex h-4.5 items-center">
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="mt-2 flex h-9 items-center">
        <Skeleton className="h-7 w-28" />
      </div>
      <div className="mt-2 flex h-4.5 items-center">
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
  );
}

/**
 * Rows matching the dense table row: px-3 py-2.5 around a 22px line box.
 *
 * 22px, not the 20px of a bare text line, because a Badge in a status column
 * is the taller element and every table here has one. A table of pure text
 * loads 2px per row shorter; a table with badges does not move at all.
 */
export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex} className="border-b border-border-subtle last:border-0">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <td key={columnIndex} className="px-3 py-2.5">
              <div className="flex h-5.5 items-center">
                <Skeleton className={cx('h-3', columnIndex === 0 ? 'w-32' : 'w-16')} />
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
